<template>
  <div class="play-page">
    <header class="toolbar">
      <button class="ghost" @click="$router.back()">← 返回</button>
      <h3 class="title">{{ video?.title || '影片播放' }}</h3>
    </header>

    <div class="card big-card">
      <div
        class="card-content"
        :class="{ 'single': !(isOldTp || hasTpRaw || hasTechNotes) }"
      >
        <!-- 左：播放器 -->
        <section class="player-wrap">
          <div v-if="loadingMeta" class="hint">載入影片資訊…</div>
          <template v-else>
            <div v-if="isSigning" class="hint">取得播放連結中…</div>
            <video
              ref="playerRef"
              class="player"
              :key="(video?.id || '') + ':' + signedUrl"   
              :src="signedUrl"
              controls
              controlsList="nodownload"
              playsinline
              webkit-playsinline
              autoplay
              :muted="autoplayMuted"
              preload="metadata"
              crossorigin="anonymous"
              @error="onVideoError"
            >
              <track
                v-for="t in captionTracks"
                :key="t.srclang"             
                kind="subtitles"
                :src="t.url"
                :srclang="t.srclang"
                :label="t.label"
                :default="t.srclang === preferredTrack"
              />
            </video>
          </template>
        </section>

        <!-- 右側內容 -->
        <aside class="side" v-if="isOldTp || hasTpRaw || hasTechNotes">
          <!-- 教學重點 or 直接原文 -->
          <section v-if="isOldTp || hasTpRaw" class="block">
            <template v-if="isOldTp">
              <h4 class="sec-title">本段教學重點：</h4>
              <div class="tp-list">
                <div v-for="(it,i) in tpItems" :key="'tp'+i" class="tp-item">
                  <template v-if="it.label">
                    <strong class="label">{{ it.label }}：</strong>
                    <span class="val">{{ it.value }}</span>
                  </template>
                  <template v-else>
                    {{ it.raw }}
                  </template>
                </div>
              </div>
            </template>

            <!-- 新格式：直接顯示原文，含標題 -->
            <template v-else>
              <h4 class="sec-title">影片介紹：</h4>
              <div class="prewrap">{{ tpRawText }}</div>
            </template>
          </section>

          <section v-if="hasTechNotes" class="block">
            <h4 class="sec-title">技術說明：</h4>
            <ul v-if="techList.length" class="tn-list">
              <li v-for="(line,i) in techList" :key="'tn'+i">{{ line }}</li>
            </ul>
            <div v-else class="prewrap">{{ video.tech_notes }}</div>
          </section>
        </aside>

      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { useRoute } from 'vue-router'
import { supabase } from '@/lib/supabase'
import i18n from '@/i18n'

const route = useRoute()
const id = route.params.id

// === 自動播放控制 ===
const autoplayMuted = ref(false)
let removeGestureListeners = () => {}

// === 語言 ===
const currentLocale = computed(() => {
  const l = i18n.global.locale
  return typeof l === 'string' ? l : (l?.value || 'zh-TW')
})

// === 影片狀態 ===
const video = ref(null)
const loadingMeta = ref(false)
const isSigning = ref(false)
const signedUrl = ref('')
const playerRef = ref(null)
let signingToken = 0

// === 字幕（僅此影片有的語言）===
const captionTracks = ref([])           // [{ srclang, label, url }]
const availableLocales = ref([])        // 來自 video_i18n 的實際語言清單

const LABEL_MAP = {
  'zh-TW': '繁體中文',
  'zh-CN': '简体中文',
  'en': 'English',
  'ja-JP': '日本語',
  'vi-VN': 'Tiếng Việt',
  'th-TH': 'ไทย'
}

const preferredTrack = computed(() => {
  const L = currentLocale.value
  if (availableLocales.value.includes(L)) return L
  return availableLocales.value[0] || '' // 沒字幕就回空
})

// =========================
// 自動播放＆字幕輔助
// =========================
async function attemptAutoplayWithFallback() {
  const v = playerRef.value
  if (!v) return
  try {
    v.muted = false
    autoplayMuted.value = false
    await v.play()
  } catch (err) {
    v.muted = true
    autoplayMuted.value = true
    try {
      await v.play()
    } catch (err2) {
      if (process.env.NODE_ENV !== 'production') {
        console.debug('Autoplay failed muted:', err2)
      }
    }
    attachFirstGestureUnmute()
  }
}

function attachFirstGestureUnmute() {
  const handler = () => {
    const v = playerRef.value
    if (!v) return
    v.muted = false
    autoplayMuted.value = false
    v.volume = 1
    v.play().catch(err => {
      if (process.env.NODE_ENV !== 'production') {
        console.debug('play() after gesture failed:', err)
      }
    })
    window.removeEventListener('click', handler)
    window.removeEventListener('touchstart', handler)
  }
  window.addEventListener('click', handler, { once: true })
  window.addEventListener('touchstart', handler, { once: true })
  removeGestureListeners = () => {
    window.removeEventListener('click', handler)
    window.removeEventListener('touchstart', handler)
  }
}

function enablePreferredCaptions() {
  const v = playerRef.value
  const tracks = v?.textTracks
  if (!tracks || !tracks.length) return

  const want = (preferredTrack.value || '').toLowerCase()
  let opened = false

  for (let i = 0; i < tracks.length; i++) {
    const tr = tracks[i]
    const lang = (tr.language || '').toLowerCase()
    if (!opened && want && lang === want) {
      tr.mode = 'showing'
      opened = true
    } else {
      tr.mode = 'disabled'
    }
  }
  if (!opened && tracks.length) {
    tracks[0].mode = 'showing'
    for (let i = 1; i < tracks.length; i++) tracks[i].mode = 'disabled'
  }
}

// =========================
// 教學重點 / 技術說明（原樣）
// =========================
const tpItems = computed(() => {
  const raw = video.value?.teaching_points
  let lines = []
  if (!raw) lines = []
  else if (Array.isArray(raw)) lines = raw.map(String)
  else if (typeof raw === 'string') {
    try {
      const j = JSON.parse(raw)
      lines = Array.isArray(j) ? j.map(String) : raw.split(/\r?\n+/)
    } catch { lines = raw.split(/\r?\n+/) }
  }
  return lines
    .map(s => s.replace(/^\s*[-*•‧・\d.]+\s*/, '').trim())
    .filter(Boolean)
    .map(s => {
      const idx = s.indexOf('：')
      if (idx > 0) return { label: s.slice(0, idx).trim(), value: s.slice(idx + 1).trim(), raw: s }
      return { label: '', value: '', raw: s }
    })
})

const tpRawText = computed(() => {
  const raw = video.value?.teaching_points
  if (!raw) return ''
  let text = ''
  if (Array.isArray(raw)) text = raw.map(String).join('\n')
  else if (typeof raw === 'string') {
    try {
      const j = JSON.parse(raw)
      text = Array.isArray(j) ? j.map(String).join('\n') : raw
    } catch { text = raw }
  } else text = String(raw ?? '')
  return text.replace(/<br\s*\/?>/gi, '\n').replace(/\r\n/g, '\n').trim()
})

const isOldTp = computed(() => {
  const t = tpRawText.value
  if (!t) return false
  const lines = t.split('\n').map(s => s.trim()).filter(Boolean)
  if (!lines.length) return false
  const colonLine = (s) => {
    const m = s.match(/^(.+?)：(.+)$/)
    return !!(m && m[1].trim() && m[2].trim())
  }
  const cnt = lines.filter(colonLine).length
  return cnt >= 2 && cnt >= Math.ceil(lines.length / 2)
})

const hasTpRaw = computed(() => !!tpRawText.value)

const hasTechNotes = computed(() =>
  typeof video.value?.tech_notes === 'string' && video.value.tech_notes.trim().length > 0
)

const techList = computed(() => {
  const t = video.value?.tech_notes || ''
  const bulletRe = /^[-*•‧・]\s*/
  const lines = t.split(/\r?\n+/).map(s => s.trim())
  const looksLikeList = lines.some(s => bulletRe.test(s))
  if (!looksLikeList) return []
  return lines.filter(s => s.length).map(s => s.replace(bulletRe, '')).filter(Boolean)
})

// =========================
// Supabase 簽名與字幕路徑
// =========================
function mapLocaleToCaptionFile(lang) {
  switch (lang) {
    case 'zh-TW': return 'zh_TW'
    case 'zh-CN': return 'zh_CN'
    case 'ja-JP': return 'ja_JP'
    case 'vi-VN': return 'vi_VN'
    case 'th-TH': return 'th_TH'
    case 'en':
    default: return 'en'
  }
}

// A/B/C.mp4 → 嘗試 A/B/C/<lang>.vtt、A/B/<lang>.vtt、A/B/C.<lang>.vtt、A/B/C_<lang>.vtt
function getCaptionPathCandidates(storagePath, langFileCode) {
  if (!storagePath) return []
  const m = String(storagePath).match(/^(.*)\/([^/]+)\.mp4$/i)
  if (!m) return []
  const dir = m[1]
  const base = m[2]
  return [
    `${dir}/${base}/${langFileCode}.vtt`,
    `${dir}/${langFileCode}.vtt`,
    `${dir}/${base}.${langFileCode}.vtt`,
    `${dir}/${base}_${langFileCode}.vtt`,
  ]
}

async function signCaptionFor(lang) {
  const code = mapLocaleToCaptionFile(lang)
  const candidates = getCaptionPathCandidates(video.value?.storage_path, code)
  for (const path of candidates) {
    const { data, error } = await supabase.storage.from('videos').createSignedUrl(path, 60 * 60)
    if (data?.signedUrl && !error) return data.signedUrl
  }
  return ''
}

// =========================
// 載入影片資訊 + 語言清單
// =========================
async function loadMeta() {
  loadingMeta.value = true

  const { data: base, error: e1 } = await supabase
    .from('videos')
    .select('id, storage_path, created_at, sort, thumb_url')
    .eq('id', id).single()

  if (e1 || !base) {
    loadingMeta.value = false
    video.value = null
    return
  }

  const { data: langsRows } = await supabase
    .from('video_i18n')
    .select('locale')
    .eq('video_id', id)

  availableLocales.value = Array.from(new Set((langsRows || []).map(r => r.locale).filter(Boolean)))

  // 抓文案（僅 UI 顯示；不影響播放）
  const L = currentLocale.value
  const needLocales = Array.from(new Set([L, 'zh-TW']))
  const { data: rows } = await supabase
    .from('video_i18n')
    .select('video_id, locale, title, teaching_points, tech_notes, thumb_url')
    .eq('video_id', id)
    .in('locale', needLocales)

  let title = ''
  let teaching_points = null
  let tech_notes = ''
  let thumb = base.thumb_url || ''
  const by = (loc) => (rows || []).find(r => r.locale === loc)
  const zh = by('zh-TW')
  const cur = by(L)
  if (zh) { title = zh.title || title; teaching_points = zh.teaching_points ?? teaching_points; tech_notes = zh.tech_notes || tech_notes; thumb = zh.thumb_url || thumb }
  if (cur) { title = cur.title || title; teaching_points = (cur.teaching_points ?? teaching_points); tech_notes = cur.tech_notes || tech_notes; thumb = cur.thumb_url || thumb }

  video.value = { id: base.id, storage_path: base.storage_path, created_at: base.created_at, sort: base.sort, title, teaching_points, tech_notes, thumb_url: thumb }

  loadingMeta.value = false
}

// =========================
// 先播影片 → 背景載字幕 → 套用
// =========================
async function signAndPlay() {
  if (!video.value?.storage_path) return
  isSigning.value = true
  signedUrl.value = ''
  captionTracks.value = []
  const token = ++signingToken

  const objectPath = String(video.value.storage_path).trim().replace(/^\/+/, '')

  try {
    // 1) 影片簽名並開播
    const { data, error } = await supabase.storage
      .from('videos')
      .createSignedUrl(objectPath, 60 * 60)
    if (token !== signingToken) return
    if (error || !data?.signedUrl) throw new Error(error?.message || 'no signedUrl')
    signedUrl.value = data.signedUrl

    await nextTick()
    if (playerRef.value?.load) playerRef.value.load()
    playerRef.value?.addEventListener('loadeddata', attemptAutoplayWithFallback, { once: true })
    attachFirstGestureUnmute()

    // 2) 背景載字幕（只針對 availableLocales）
    if (availableLocales.value.length) {
      
      // 併發簽名，速度快很多
      const results = await Promise.all(
        availableLocales.value.map(async loc => {
          const url = await signCaptionFor(loc)
          return url ? { srclang: loc, label: LABEL_MAP[loc] || loc, url } : null
        })
      )

      // 寫入 <track> 清單（不呼叫 player.load()，避免重播）
      captionTracks.value = results.filter(Boolean)

      // 等 DOM 插入完，再開啟偏好字幕
      await nextTick()
      enablePreferredCaptions()
    }

  } catch (e) {
    if (token !== signingToken) return
    alert('產生簽名網址失敗：' + (e.message || e))
  } finally {
    if (token === signingToken) isSigning.value = false
  }
}

function onVideoError(e) {
  const code = e?.target?.error?.code
  console.error('[Video error]', { code, signedUrl: signedUrl.value, video: video.value })
  if (!signedUrl.value) return
  alert('影片載入失敗，請稍後重試（代碼 ' + code + '）')
}

// ===== lifecycle =====
async function boot() {
  await loadMeta()      // 先知道有哪幾種語言 & 文案
  await signAndPlay()   // 立即播影片；字幕背景補上
}

onMounted(boot)
onUnmounted(() => removeGestureListeners())

watch(currentLocale, async (n, o) => {
  if (n !== o) await boot()
})
</script>

<style scoped>
/* =========================
   Layout
   ========================= */
.play-page {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.card-content {
  display: grid;
  grid-template-columns: 2fr 1fr;  /* 預設左右 2:1 */
  gap: 20px;                       /* 左右間隔 */
  align-items: start;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px 20px;              /* ⬅️ 文字區塊 padding 更大 */
}

/* 如果沒有文字，影片就全寬 */
.card-content.single {
  grid-template-columns: 1fr;
}

/* 手機版：強制上下疊 */
@media (max-width: 960px) {
  .card-content {
    grid-template-columns: 1fr;
  }
}

/* =========================
   Toolbar
   ========================= */
.toolbar {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  gap: 12px;
}
.title {
  margin: 0;
  font-weight: 600;
}

/* =========================
   Player Area
   ========================= */
.player-wrap {
  min-height: 360px;
}
.player {
  width: 100%;
  aspect-ratio: 16/9;
  background: #000;
  border-radius: 10px;
  display: block;
}
.hint {
  color: #6b7280;
  padding: 18px;
}

/* =========================
   Side / Text Cards
   ========================= */
.side {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.side .card {
  text-align: left;
}

/* Section 標題 */
.sec-title {
  margin: 0 0 10px;
  color: #2563eb;
  font-weight: 700;
  font-size: 16px;
}

.prewrap {
  white-space: pre-wrap;
  line-height: 1.7;
  color: #1f2937;
  text-align: left; /* ⬅️ 強制靠左 */
}

/* =========================
   Teaching Points（無圓點）
   ========================= */
.tp-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.tp-item {
  text-align: left;
  line-height: 1.7;
}
.label {
  font-weight: 700;
  color: #1f2937;
}
.val {
  color: #1f2937;
}

/* =========================
   Tech Notes（Markdown 大黑點）
   ========================= */
.tn-list {
  margin: 0;
  padding-left: 0;
  list-style: none;
  text-align: left;
}
.tn-list li {
  position: relative;
  margin: 6px 0;
  line-height: 1.8;
  padding-left: 1.3em;
  color: #1f2937;
  font-size: 1rem;
}
.tn-list li::before {
  content: "";
  position: absolute;
  left: 0.2em;
  top: 0.72em;
  width: 0.46em;
  height: 0.46em;
  border-radius: 50%;
  background: #111827;
}


/* =========================
   Button
   ========================= */
.ghost {
  background: #fff;
  color: #374151;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 6px 10px;
}
</style>
